import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useFetch from "../../../../hooks/useFetch";

import { calcIQ } from '../utils/iqUtils';
import { playSound } from '../utils/sounds';

export function useIQGame() {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const token = authState.token;
  const userId = authState?.user?._id;

  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [questionsError, setQuestionsError] = useState(null);

  const [screen, setScreen] = useState('home');
  const [currentSet, setCurrentSet] = useState(1);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [currentQ, setCurrentQ] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15);
  const [feedback, setFeedback] = useState(null);
  const [streak, setStreak] = useState(0);
  const [bestScore, setBestScore] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [streakHistory, setStreakHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  // previously used but never declared — added:
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [streakSaved, setStreakSaved] = useState(false);
  const [streakConfirmData, setStreakConfirmData] = useState(null);
  const [streakMessage, setStreakMessage] = useState(null);
  const [timeTaken, setTimeTaken] = useState(0);

  const timerRef = useRef(null);
  const qStartRef = useRef(null);
  const feedbackTimeoutRef = useRef(null);
  const gameDataRef = useRef({ correct: 0, xp: 0, answerTimes: [] });
  const gameStartTimeRef = useRef(0);

  const TOTAL = questions?.length || 0;

  // ── Fetch Questions ─────────────────────────────
  const fetchQuestions = async (level, set) => {
    try {
      setLoadingQuestions(true);
      setQuestionsError(null);

      const config = {
        url: "/iq/questions",
        method: "post",
        data: { level, set },
        headers: { Authorization: token },
      };

      const response = await fetchData(config, { silent: true });
      const questionsList = response?.questions || response?.data || response;

      if (Array.isArray(questionsList)) {
        const randomized = randomizeQuestions(questionsList);
        setQuestions(randomized);
      } else {
        setQuestionsError("Invalid questions data");
      }
    } catch (error) {
      console.error("Questions Fetch Error:", error);
      setQuestionsError("Failed to load questions");
    } finally {
      setLoadingQuestions(false);
    }
  };

  // ── Fetch My Stats (single definition — was declared twice) ─────────
  const fetchMyStats = async () => {
    if (!userId) return;
    try {
      const config = {
        url: "/iq/me",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      };

      const response = await fetchData(config, { silent: true });
      if (response?.success || response?.status) {
        setMyStats(response);
        setStreakHistory(response?.streakHistory || []);
        setBestScore(response.bestScore || null);
        // Server is now the source of truth for streak (was localStorage,
        // which also wasn't scoped per-user — a shared-browser bug).
        setStreak(response?.streakHistory?.[0]?.streakDays || 0);
      }
    } catch (e) {
      console.error("Stats fetch error:", e);
      setStreakHistory([]);
      setBestScore(null);
    }
  };

  // ── Fetch Leaderboard (single definition — was declared twice) ──────
  const fetchLeaderboard = async () => {
    try {
      const config = {
        url: "/iq/leaderboard",
        method: "post",
        headers: { Authorization: token },
      };

      const response = await fetchData(config, { silent: true });

      if (response?.success) {
        setLeaderboard(response.leaderboard || []);
      } else {
        setLeaderboard([]);
      }

      return response;
    } catch (err) {
      console.error(err);
      setLeaderboard([]);
      return { success: false, leaderboard: [] };
    }
  };

  // ── Submit score to backend (was called but never defined) ──────────
  const submitScore = async (payload) => {
    const config = {
      url: "/iq/submit",
      method: "post",
      data: { ...payload, userId },
      headers: { Authorization: token },
    };
    return fetchData(config, { silent: true });
  };

  // Initial load — questions + stats whenever token becomes available
  useEffect(() => {
    if (token) {
      fetchQuestions(currentLevel, currentSet);
      fetchMyStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

  const randomizeQuestions = (list) => {
    return shuffleArray(list).map((q) => ({
      ...q,
      options: shuffleArray(q.options || []),
    }));
  };

  // ── Load stats on mount — streak comes from the server response above,
  // not localStorage (see fetchMyStats) ──
  useEffect(() => {
    if (userId) {
      fetchMyStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const clearTimer = () => clearInterval(timerRef.current);

  const handleTimeout = (q) => {
    if (answered) return;

    playSound("timeout");
    setAnswered(true);

    gameDataRef.current.answerTimes.push(q.time || 15);

    setFeedback({
      correct: false,
      xpEarned: 0,
      title: "Time's up ⏰",
      sub: "The answer was: " + q.answer,
      correctAnswer: q.answer,
    });

    feedbackTimeoutRef.current = setTimeout(() => {
      setFeedback(null);

      if (currentQ + 1 >= TOTAL) {
        finishGame();
      } else {
        setCurrentQ((c) => c + 1);
      }
    }, 1600);
  };

  const startTimer = (q) => {
    clearTimer();

    let t = q.time || 15;
    setTimeLeft(t);

    timerRef.current = setInterval(() => {
      t--;
      setTimeLeft(t);

      if (t <= 0) {
        clearTimer();
        handleTimeout(q);
      }
    }, 1000);
  };

  const startGame = useCallback(
    async (level = currentLevel, set = currentSet) => {
      setCurrentLevel(level);
      setCurrentSet(set);

      gameDataRef.current = { correct: 0, xp: 0, answerTimes: [] };

      await fetchQuestions(level, set);
      setCurrentQ(0);

      gameStartTimeRef.current = Date.now();
      setAnswered(false);
      setSelectedOpt(null);
      setFeedback(null);
      setSubmitResult(null);
      setStreakSaved(false);
      setStreakConfirmData(null);
      setStreakMessage(null);

      setScreen("game");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLevel, currentSet]
  );

  const loadQuestion = (idx) => {
    if (!questions[idx]) return;

    setAnswered(false);
    setSelectedOpt(null);
    setFeedback(null);

    qStartRef.current = Date.now();

    clearTimer();
    feedbackTimeoutRef.current && clearTimeout(feedbackTimeoutRef.current);
    startTimer(questions[idx]);
  };

  useEffect(() => {
    if (screen !== "game") return;
    if (!questions[currentQ]) return;

    loadQuestion(currentQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, currentQ, questions]);

  const selectAnswer = useCallback(
    (opt) => {
      if (answered || !questions[currentQ]) return;

      clearTimer();
      setAnswered(true);
      setSelectedOpt(opt);

      const q = questions[currentQ];
      const isCorrect = opt === q.answer;
      const elapsed = (Date.now() - qStartRef.current) / 1000;

      gameDataRef.current.answerTimes.push(elapsed);

      const speedBonus = isCorrect ? Math.max(0, Math.round((q.time - elapsed) * 5)) : 0;
      const levelBonus = (q.level || 1) * 10;
      const xpEarned = isCorrect ? levelBonus + speedBonus : 0;

      if (isCorrect) {
        playSound('correct');
        gameDataRef.current.correct += 1;
        gameDataRef.current.xp += xpEarned;
      } else {
        playSound('wrong');
      }

      const correctMsgs = [
        ['Absolutely cooked 🔥', "You're built different"],
        ['Big brain moment 🧠', 'No cap, that was fast'],
        ['Sheesh 😤', 'Elite IQ activity'],
        ['Slay ✨', 'Brain so wrinkled rn'],
        ['IYKYK 🎯', 'That one was a vibe'],
      ];
      const wrongMsgs = [
        ['It happens 😮‍💨', 'Even geniuses fumble'],
        ['Not it 🫣', 'The answer was: ' + q.answer],
        ['Close-ish 😬', 'You got this next one'],
        ['L moment 💀', 'Glow-up loading...'],
      ];

      const msgs = isCorrect ? correctMsgs : wrongMsgs;
      const [title, sub] = msgs[Math.floor(Math.random() * msgs.length)];

      setFeedback({ correct: isCorrect, xpEarned, title, sub, correctAnswer: q.answer });

      feedbackTimeoutRef.current = setTimeout(() => {
        setFeedback(null);
        if (currentQ + 1 >= TOTAL) {
          finishGame();
        } else {
          setCurrentQ((c) => c + 1);
        }
      }, 1600);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [answered, currentQ, questions, TOTAL]
  );

  const finishGame = async () => {
    if (screen !== "game") return;
    clearTimer();

    const elapsedTime = Date.now() - gameStartTimeRef.current;
    setTimeTaken(elapsedTime);

    const g = gameDataRef.current;
    const times = g.answerTimes;
    const avgSpeed = times.length ? +(times.reduce((a, b) => a + b, 0) / times.length).toFixed(1) : null;

    const iq = calcIQ(g.correct, TOTAL, avgSpeed);
    playSound('complete');

    if (userId) {
      setSubmitting(true);
      try {
        const response = await submitScore({
          iqScore: iq,
          level: TOTAL,
          correct: g.correct,
          totalQuestions: TOTAL,
          xp: g.xp,
          avgSpeed,
        });
        setSubmitResult(response);
      } catch (e) {
        console.warn('Score submit error', e);
      } finally {
        setSubmitting(false);
      }
    }

    setStreakConfirmData({
      streak,
      iq,
      xp: g.xp,
      correct: g.correct,
      avgSpeed,
      timeTaken: elapsedTime,
    });
    setScreen('streakConfirm');
  };

  const stopGame = () => {
    clearTimer();
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
  };

  // NOTE: `timeTaken` is computed as a local const FIRST, before it's used
  // in `canSave` below. Previously it was used before being declared later
  // in the same function — that throws "Cannot access before initialization".
  const handleStreakConfirm = async (save) => {
    const elapsed = Date.now() - gameStartTimeRef.current;
    setTimeTaken(elapsed);
    setScreen('results');
    setStreakMessage(null);

    if (!save || !streakConfirmData) return;

    const previous = streakHistory.find(
      (s) => s.currentLevel === currentLevel && s.currentSet === currentSet
    );

    const canSave =
      streakConfirmData.correct > 0 &&
      (!previous ||
        streakConfirmData.iq > previous.iqAtTime ||
        (streakConfirmData.iq === previous.iqAtTime && elapsed < previous.timeTaken));

    if (!canSave) {
      setStreakMessage("❌ Score cannot be saved.");
      return;
    }

    try {
      const payload = {
        iqAtTime: streakConfirmData.iq,
        currentSet,
        currentLevel,
        timeTaken: elapsed,
        xp: streakConfirmData.xp,
        userId,
        userCode: authState?.user?.think5whyscode || "",
        userName: authState?.user?.name || "User",
      };
      const config = {
        url: "/iq/streaks/confirm",
        method: "post",
        data: payload,
        headers: { Authorization: token },
      };

      const response = await fetchData(config, { silent: false });

      if (response?.success) {
        setStreakSaved(true);
        playSound('streak');
        await fetchMyStats();
        setStreakMessage("🎉 Score saved successfully!");
      } else {
        setStreakMessage("Failed to save streak");
      }
    } catch (e) {
      console.error("Streak confirm error:", e);
      setStreakMessage("Failed to save streak");
    }
  };

  useEffect(() => {
    return () => stopGame();
  }, []);

  return {
    screen, setScreen,
    currentQ,
    timeLeft,
    timeTotal: questions[currentQ]?.time || 15,
    question: questions[currentQ],
    answered,
    selectedOpt,
    feedback,
    streak,
    bestScore,
    currentLevel,
    currentSet,
    leaderboard,
    streakHistory,
    myStats,
    loading: loadingQuestions,
    questionsError,
    TOTAL,

    // was previously shadowed by a no-op stub in the return object — fixed
    startGame,
    selectAnswer,
    finishGame,
    stopGame,
    handleStreakConfirm,

    submitting,
    submitResult,
    streakSaved,
    streakConfirmData,
    streakMessage,
    timeTaken,

    fetchMyStats,
    fetchQuestions,
    fetchLeaderboard,
  };
}